/**
 * Simulation History Panel Component
 *
 * Displays past simulation runs stored in IndexedDB, allowing users to
 * browse history and load previous results.
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  getSimulationHistory,
  getSimulationHistoryEntry,
  deleteSimulationHistoryEntry,
  clearSimulationHistory,
  type SimulationHistoryEntry,
} from '@/simulation/simulationHistoryCache';
import { useSimulationStore } from '@/simulation/simulationStore';

interface SimulationHistoryPanelProps {
  onLoadResults?: (() => void) | undefined;
}

export function SimulationHistoryPanel({ onLoadResults }: SimulationHistoryPanelProps) {
  const [history, setHistory] = useState<SimulationHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const setComplete = useSimulationStore((s) => s.setComplete);

  // Load history on mount
  useEffect(() => {
    async function loadHistory() {
      setIsLoading(true);
      setError(null);
      try {
        const entries = await getSimulationHistory();
        setHistory(entries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load history');
      } finally {
        setIsLoading(false);
      }
    }
    void loadHistory();
  }, []);

  const handleLoadEntry = useCallback(
    async (id: string) => {
      setLoadingId(id);
      setError(null);
      try {
        console.log('[SimulationHistoryPanel] Loading entry:', id);
        const entry = await getSimulationHistoryEntry(id);
        if (!entry) {
          throw new Error('Simulation entry not found');
        }

        if (!entry.output) {
          throw new Error('No output data stored for this simulation');
        }

        // Debug: log the loaded data structure
        console.log('[SimulationHistoryPanel] Loaded entry:', {
          hasOutput: !!entry.output,
          hasEso: !!entry.output?.eso,
          esoVariablesType: entry.output?.eso?.variables?.constructor?.name,
          esoVariablesIsMap: entry.output?.eso?.variables instanceof Map,
          variableCount: entry.output?.eso?.variables instanceof Map
            ? entry.output.eso.variables.size
            : 'N/A (not a Map)',
        });

        // Load the results into the simulation store using setComplete
        // This will set status to 'complete' and extract available variables from output
        setComplete(entry.output);
        console.log('[SimulationHistoryPanel] setComplete called, store updated');

        onLoadResults?.();
      } catch (err) {
        console.error('[SimulationHistoryPanel] Load error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load simulation');
      } finally {
        setLoadingId(null);
      }
    },
    [setComplete, onLoadResults]
  );

  const handleDeleteEntry = useCallback(async (id: string) => {
    try {
      await deleteSimulationHistoryEntry(id);
      setHistory((prev) => prev.filter((e) => e.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  }, []);

  const handleClearAll = useCallback(async () => {
    try {
      await clearSimulationHistory();
      setHistory([]);
      setShowClearConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear history');
    }
  }, []);

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const d = new Date(date);
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Simulation History</h3>
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Simulation History</h3>
        {history.length > 0 && (
          <div>
            {showClearConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Clear all?</span>
                <Button variant="outline" size="sm" onClick={() => setShowClearConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={handleClearAll}
                >
                  Clear
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowClearConfirm(true)}>
                Clear All
              </Button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {history.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mx-auto mb-3 opacity-50"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <p>No simulation history yet</p>
          <p className="text-sm mt-1">Run a simulation to see it here</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {history.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      entry.success ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="font-medium truncate">{entry.input.idfName}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{formatDate(entry.timestamp)}</span>
                  <span>{formatDuration(entry.duration)}</span>
                  <span>{entry.summary.variableCount} vars</span>
                  {entry.summary.severeErrors !== undefined && entry.summary.severeErrors > 0 && (
                    <span className="text-red-500">
                      {entry.summary.severeErrors} errors
                    </span>
                  )}
                  {entry.summary.warnings !== undefined && entry.summary.warnings > 0 && (
                    <span className="text-yellow-600">
                      {entry.summary.warnings} warnings
                    </span>
                  )}
                </div>
                {entry.error && (
                  <p className="text-xs text-red-500 mt-1 truncate">{entry.error}</p>
                )}
              </div>

              <div className="flex items-center gap-2 ml-4">
                {deleteConfirmId === entry.id ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteConfirmId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteEntry(entry.id)}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteConfirmId(entry.id)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleLoadEntry(entry.id)}
                      disabled={loadingId === entry.id || !entry.success}
                    >
                      {loadingId === entry.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        'Load'
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4 text-center">
        Up to 20 simulations are stored locally
      </p>
    </div>
  );
}
